import { ThreeDots } from '@toggle/design-system';
import React from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';

import { ChatQuestionResponse } from '~/components/chat-question-response/ChatQuestionResponse';
import { Tracking } from '~/services/tracking';

import { Message } from '..//use-chat/use-chat.types';
import { EmptyResponse } from './EmptyResponse';
import * as S from './Messages.styles';

export interface MessagesProps {
  messages: Message[];
}

export const syntaxHighlight = (json: string) => {
  if (!json) {
    return '';
  } //no JSON from response

  json = json
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  return json.replace(
    /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?)/g,
    function (match) {
      let cls = 'number';

      if (/^"/.test(match)) {
        if (/:$/.test(match)) {
          cls = 'key';
        } else {
          cls = 'string';
        }
      } else if (/true|false/.test(match)) {
        cls = 'boolean';
      } else if (/null/.test(match)) {
        cls = 'null';
      }

      return '<span class="' + cls + '">' + match + '</span>';
    }
  );
};

export const Messages = ({ messages }: MessagesProps) => {
  const { t } = useTranslation('intel');

  const logError = (error: Error, errorInfo: React.ErrorInfo) => {
    Tracking.captureException(error, { extra: { errorInfo } });
  };

  return (
    <>
      {messages.map((message, i) => {
        const isDocumentAssistantTargeted =
          message.target === 'document-assistant';
        const isQuestionPending = message.status === 'pending';
        const messageAborted = message.status === 'abort';
        const isLastMessage = i === messages.length - 1;

        return (
          <S.Root
            key={message.id}
            shouldScroll={isLastMessage}
            shouldUpdate={message.status === 'complete'}
          >
            {message.type === 'reset' ? (
              <S.NewSession>
                <span>{t('intel:tryAgain')}</span>
              </S.NewSession>
            ) : (
              <ChatQuestionResponse
                title={message.question}
                isQuestionPending={false}
                isDocumentMessage={false}
              >
                {message.reply ? (
                  <ErrorBoundary
                    fallback={<p>{t('intel:empty')}</p>}
                    onError={logError}
                  >
                    <S.StyledPre
                      dangerouslySetInnerHTML={{
                        __html: syntaxHighlight(
                          JSON.stringify(message.reply, undefined, 4)
                        ),
                      }}
                    />
                    {isQuestionPending && <ThreeDots />}
                  </ErrorBoundary>
                ) : (
                  <EmptyResponse
                    messageAborted={messageAborted}
                    messageIndex={i}
                    isDocumentMessage={isDocumentAssistantTargeted}
                  />
                )}

                {messageAborted && (
                  <S.AbortMessage>{t('intel:abortMessage')}</S.AbortMessage>
                )}
              </ChatQuestionResponse>
            )}
          </S.Root>
        );
      })}
    </>
  );
};
